import React, { useCallback, useEffect, useRef } from "react";

const COL_COUNTS = [6, 7, 6, 7, 6, 7, 6];

function nodeKind(col, nCols) {
  if (col === 0 || col === nCols - 1) return "io";
  return col % 2 === 1 ? "hiddenA" : "hiddenB";
}

function graphRectInAnchor(anchorEl, slotEl) {
  if (!anchorEl || !slotEl) return null;
  const a = anchorEl.getBoundingClientRect();
  const s = slotEl.getBoundingClientRect();
  const w = s.width;
  const h = s.height;
  if (w < 8 || h < 8) return null;
  return {
    x: s.left - a.left,
    y: s.top - a.top,
    w,
    h,
  };
}

/**
 * Hero-визуал: космические квадратные «звёзды» на весь anchor + сеть в области graphSlot;
 * без pointer/touch (не мешает скроллу). prefers-reduced-motion.
 */
export default function HeroNetworkCanvas({
  theme,
  isMobile,
  isGuruDomain = false,
  isSamsungBrowser = false,
  anchorRef,
  graphSlotRef,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const reduceMotionRef = useRef(false);
  const rafRef = useRef(0);

  const isDark = theme === "dark";

  const pickCosmicPalette = useCallback(() => {
    if (isGuruDomain) {
      return {
        mint: isDark ? "rgba(255, 214, 140, 0.9)" : "rgba(200, 160, 70, 0.85)",
        lavender: isDark ? "rgba(255, 235, 190, 0.75)" : "rgba(180, 150, 90, 0.7)",
        sky: isDark ? "rgba(255, 200, 120, 0.65)" : "rgba(160, 130, 60, 0.65)",
      };
    }
    return {
      mint: isDark ? "rgba(94, 234, 212, 0.88)" : "rgba(45, 212, 191, 0.75)",
      lavender: isDark ? "rgba(199, 210, 254, 0.82)" : "rgba(129, 140, 248, 0.55)",
      sky: isDark ? "rgba(147, 197, 253, 0.78)" : "rgba(96, 165, 250, 0.55)",
    };
  }, [isDark, isGuruDomain]);

  const pickColors = useCallback(() => {
    if (isGuruDomain) {
      return {
        line: isDark ? "rgba(200, 170, 90, 0.26)" : "rgba(136, 113, 43, 0.32)",
        lineBright: isDark ? "rgba(235, 205, 140, 0.55)" : "rgba(136, 113, 43, 0.55)",
        nodeA: isDark ? "rgba(220, 195, 120, 0.72)" : "rgba(190, 160, 85, 0.62)",
        nodeB: isDark ? "rgba(255, 252, 245, 0.98)" : "rgba(255, 252, 245, 0.98)",
        nodeIoRing: isDark ? "rgba(240, 200, 110, 0.98)" : "rgba(136, 113, 43, 0.95)",
        nodeIoFill: isDark ? "rgba(30, 28, 40, 0.92)" : "rgba(255, 255, 255, 0.92)",
        packet: isDark ? "rgba(255, 235, 170, 0.95)" : "rgba(160, 130, 60, 0.95)",
      };
    }
    return {
      line: isDark ? "rgba(129, 140, 248, 0.24)" : "rgba(99, 102, 241, 0.26)",
      lineBright: isDark ? "rgba(190, 200, 255, 0.55)" : "rgba(99, 102, 241, 0.48)",
      nodeA: isDark ? "rgba(165, 175, 255, 0.78)" : "rgba(129, 140, 248, 0.58)",
      nodeB: isDark ? "rgba(252, 252, 255, 0.98)" : "rgba(255, 255, 255, 0.95)",
      nodeIoRing: isDark ? "rgba(200, 185, 255, 0.98)" : "rgba(99, 102, 241, 0.9)",
      nodeIoFill: isDark ? "rgba(15, 23, 42, 0.9)" : "rgba(255, 255, 255, 0.94)",
      packet: isDark ? "rgba(220, 210, 255, 0.96)" : "rgba(120, 110, 220, 0.92)",
    };
  }, [isDark, isGuruDomain]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduceMotionRef.current = mq.matches;
    const onChange = () => {
      reduceMotionRef.current = mq.matches;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const particles = [];
    const PARTICLE_COUNT = isMobile ? 10 : 18;
    let colsLayout = [];
    let edges = [];
    let cosmicParticles = [];

    const buildCosmic = (w, h) => {
      cosmicParticles = [];
      const area = w * h;
      const n = Math.min(240, Math.max(56, Math.floor(area / 3400)));
      for (let i = 0; i < n; i++) {
        cosmicParticles.push({
          nx: Math.random(),
          ny: Math.random(),
          z: 0.25 + Math.random() * 0.75,
          s: 0.7 + Math.random() * 2.4,
          phase: Math.random() * Math.PI * 2,
          kind: (Math.random() * 3) | 0,
          rot: Math.random() * Math.PI,
        });
      }
    };

    const buildGraph = (graphX, graphY, gw, gh) => {
      const padX = gw * (isMobile ? 0.05 : 0.06);
      const padY = Math.max(isMobile ? 12 : 8, gh * (isMobile ? 0.13 : 0.1));
      const innerW = Math.max(1, gw - padX * 2);
      const innerH = Math.max(1, gh - padY * 2);
      const nCols = COL_COUNTS.length;

      colsLayout = COL_COUNTS.map((count, c) => {
        const x = graphX + padX + (c / Math.max(1, nCols - 1)) * innerW;
        const col = [];
        for (let r = 0; r < count; r++) {
          const y = graphY + padY + ((r + 1) / (count + 1)) * innerH;
          col.push({
            bx: x,
            by: y,
            kind: nodeKind(c, nCols),
            col: c,
            row: r,
            phase: c * 1.7 + r * 0.9,
          });
        }
        return col;
      });

      edges = [];
      for (let c = 0; c < colsLayout.length - 1; c++) {
        for (const a of colsLayout[c]) {
          for (const b of colsLayout[c + 1]) {
            edges.push({ a, b, hue: Math.random() * Math.PI * 2 });
          }
        }
      }

      particles.length = 0;
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const e = edges[(Math.random() * edges.length) | 0];
        if (!e) continue;
        particles.push({
          edge: e,
          t: Math.random(),
          speed: 0.018 + Math.random() * 0.032,
        });
      }
    };

    const sizeSource = () => anchorRef?.current ?? wrap;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const el = sizeSource();
      const cw = Math.max(1, el.clientWidth);
      const ch = Math.max(1, el.clientHeight);
      canvas.width = Math.floor(cw * dpr);
      canvas.height = Math.floor(ch * dpr);
      canvas.style.width = `${cw}px`;
      canvas.style.height = `${ch}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildCosmic(cw, ch);
      if (anchorRef?.current && graphSlotRef?.current) {
        const gr = graphRectInAnchor(anchorRef.current, graphSlotRef.current);
        if (gr) {
          buildGraph(gr.x, gr.y, gr.w, gr.h);
        } else {
          colsLayout = [];
          edges = [];
          particles.length = 0;
        }
      } else {
        buildGraph(0, 0, cw, ch);
      }
    };

    const ro = new ResizeObserver(() => resize());
    const el = sizeSource();
    ro.observe(el);
    if (anchorRef?.current && graphSlotRef?.current) {
      ro.observe(graphSlotRef.current);
    }
    resize();

    let start = performance.now();

    const drawCosmic = (W, H, t, reduce) => {
      if (!cosmicParticles.length) return;
      const cos = pickCosmicPalette();
      const drift = reduce ? 0 : 1;
      for (const p of cosmicParticles) {
        const px =
          p.nx * W +
          Math.sin(p.phase + t * 0.38) * 5 * drift * p.z;
        const py =
          p.ny * H +
          Math.cos(p.phase * 1.07 + t * 0.32) * 4 * drift * p.z;
        const twinkle = reduce
          ? 0.42
          : 0.16 + 0.72 * (0.5 + 0.5 * Math.sin(t * 1.35 + p.phase)) * (0.45 + 0.55 * p.z);
        const fill = p.kind === 0 ? cos.mint : p.kind === 1 ? cos.lavender : cos.sky;
        ctx.fillStyle = fill;
        ctx.globalAlpha = Math.min(0.92, twinkle * (0.45 + 0.65 * p.z));
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(p.rot + t * 0.048 * drift * (0.55 + 0.45 * p.z));
        ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    };

    const frame = (now) => {
      const t = (now - start) / 1000;
      const reduce = reduceMotionRef.current;
      const el = sizeSource();
      const W = Math.max(1, el.clientWidth);
      const H = Math.max(1, el.clientHeight);

      if (
        anchorRef?.current &&
        graphSlotRef?.current &&
        (!colsLayout.length || !edges.length)
      ) {
        const gr = graphRectInAnchor(anchorRef.current, graphSlotRef.current);
        if (gr) buildGraph(gr.x, gr.y, gr.w, gr.h);
      }

      ctx.clearRect(0, 0, W, H);
      drawCosmic(W, H, t, reduce);

      if (!colsLayout.length || !edges.length) {
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      const colors = pickColors();

      const positions = colsLayout.map((col) =>
        col.map((node) => {
          const driftAmp = reduce ? 0 : isMobile ? 2.2 : 3.2;
          const speed = reduce ? 0 : 1;
          const ox = Math.sin(t * speed * 0.55 + node.phase) * driftAmp;
          const oy = Math.cos(t * speed * 0.48 + node.phase * 1.1) * driftAmp * 0.85;
          return { x: node.bx + ox, y: node.by + oy, kind: node.kind };
        })
      );

      const pulse = reduce ? 0.26 : 0.18 + Math.sin(t * 1.25) * 0.07;

      ctx.lineWidth = isMobile ? 0.6 : 0.72;
      for (let i = 0; i < edges.length; i++) {
        const { a, b, hue } = edges[i];
        const pa = positions[a.col][a.row];
        const pb = positions[b.col][b.row];
        const flicker = reduce ? 1 : 0.88 + 0.12 * Math.sin(t * 1.55 + hue + i * 0.02);
        ctx.strokeStyle = i % 7 === 0 ? colors.lineBright : colors.line;
        ctx.globalAlpha = pulse * flicker;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      if (!reduce) {
        const step = isMobile ? 0.22 : 0.26;
        for (const p of particles) {
          p.t += p.speed * step;
          if (p.t > 1) {
            p.t = 0;
            p.edge = edges[(Math.random() * edges.length) | 0] || p.edge;
          }
          const e = p.edge;
          const pa = positions[e.a.col][e.a.row];
          const pb = positions[e.b.col][e.b.row];
          const x = pa.x + (pb.x - pa.x) * p.t;
          const y = pa.y + (pb.y - pa.y) * p.t;
          ctx.beginPath();
          ctx.fillStyle = colors.packet;
          ctx.globalAlpha = 0.62 + 0.32 * Math.sin(t * 2.8 + p.t * 6);
          ctx.arc(x, y, isMobile ? 2.5 : 3.2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      const nodeR = isMobile ? 3.8 : 4.6;
      for (let c = 0; c < positions.length; c++) {
        for (let r = 0; r < positions[c].length; r++) {
          const { x, y, kind } = positions[c][r];
          if (kind === "io") {
            ctx.beginPath();
            ctx.strokeStyle = colors.nodeIoRing;
            ctx.lineWidth = isMobile ? 1.7 : 2.1;
            ctx.fillStyle = colors.nodeIoFill;
            ctx.arc(x, y, nodeR + (isMobile ? 0.65 : 0.85), 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.beginPath();
            ctx.fillStyle = isGuruDomain
              ? isDark
                ? "rgba(255, 210, 120, 0.4)"
                : "rgba(136, 113, 43, 0.28)"
              : isDark
                ? "rgba(190, 175, 255, 0.42)"
                : "rgba(99, 102, 241, 0.26)";
            ctx.arc(x, y, nodeR * 0.45, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.beginPath();
            ctx.fillStyle = kind === "hiddenA" ? colors.nodeA : colors.nodeB;
            ctx.arc(x, y, nodeR, 0, Math.PI * 2);
            ctx.fill();
            if (!isSamsungBrowser && kind === "hiddenB") {
              ctx.strokeStyle = isDark ? "rgba(180, 195, 220, 0.35)" : "rgba(99, 102, 241, 0.16)";
              ctx.lineWidth = 0.65;
              ctx.stroke();
            }
          }
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [isMobile, isGuruDomain, isSamsungBrowser, theme, pickColors, pickCosmicPalette]);

  const useExternalAnchor = Boolean(anchorRef);

  return (
    <div
      ref={wrapRef}
      style={{
        position: useExternalAnchor ? "absolute" : "relative",
        left: useExternalAnchor ? 0 : undefined,
        top: useExternalAnchor ? 0 : undefined,
        right: useExternalAnchor ? 0 : undefined,
        bottom: useExternalAnchor ? 0 : undefined,
        width: "100%",
        height: "100%",
        minHeight: useExternalAnchor ? 0 : 0,
        borderRadius: useExternalAnchor ? 0 : isMobile ? 10 : 14,
        overflow: "hidden",
        background: "transparent",
        boxSizing: "border-box",
        pointerEvents: "none",
      }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
